import { BoxMargin } from 'tabler-icons-react';
import {
  ECOM_WEBSTORE_SECTION_BOUND_TYPE,
  ECOM_WEBSTORE_SECTION_TYPE_ENUM,
  INPUT_CONTROL_TYPE_ENUM,
  ECOM_ENTITY_TYPES,
  ECOM_WEBSTORE_SECTION_BLOCK_TYPE_ENUM,
  HEADING_SIZE_STYLE_ENUM
} from '../../../../../config/constants';
import WsCategoryList from './WsCategoryList';
import { ECOM_WEBSTORE_SECTION_BLOCKS } from '../../blocks/sectionBlockConfig';

const LIST_DISPLAY_ENUM = {
  TEXT: 'text',
  CARD: 'card'
};

const LIST_SORT_ENUM = {
  ALPHABETICAL: 'alphabetical',
  ALPHABETICAL_TOP_DOWN: 'alphabetical_top_down',
  PRODUCT_COUNT: 'product_count'
};

const CARD_SIZE_ENUM = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  EXTRA_LARGE: 'extra_large',
  HUGE: 'huge'
};

const VIEW_ALL_STYLE_ENUM = {
  LINK: 'LINK',
  OUTLINE_BUTTON: 'OUTLINE_BUTTON',
  SOLID_BUTTON: 'SOLID_BUTTON'
};

const configNameValues = {
  headingText: 'headingText',
  headingSize: 'headingSize',
  descriptionText: 'descriptionText',
  isSubcategoryList: 'isSubcategoryList',
  isSubcategoriesUnfiltered: 'isSubcategoriesUnfiltered',
  maximumDisplayed: 'maximumDisplayed',
  enableViewAll: 'enableViewAll',
  viewAllButtonStyle: 'viewAllButtonStyle',
  displayType: 'displayType',
  isOutlineStyle: 'isOutlineStyle',
  showIcons: 'showIcons',
  cardSize: 'cardSize',
  textSortBy: 'textSortBy',
  textAlign: 'textAlign',
  iconPosition: 'iconPosition',
  fontSize: 'fontSize',
  hideEmptyList: 'hideEmptyList',
  categoriesFullWidth: 'categoriesFullWidth',
  desktopColumnCount: 'desktopColumnCount',
  mobileColumnCount: 'mobileColumnCount',
  colorScheme: 'colorScheme'
};

const configDefaultValues = {
  [configNameValues.headingText]: 'Categories',
  [configNameValues.headingSize]: HEADING_SIZE_STYLE_ENUM.MEDIUM,
  [configNameValues.descriptionText]: '',
  [configNameValues.categoriesFullWidth]: 'false',
  [configNameValues.maximumDisplayed]: '6',
  [configNameValues.displayType]: LIST_DISPLAY_ENUM.CARD,
  [configNameValues.isOutlineStyle]: 'true',
  [configNameValues.showIcons]: 'true',
  [configNameValues.cardSize]: CARD_SIZE_ENUM.SMALL,
  [configNameValues.textSortBy]: LIST_SORT_ENUM.ALPHABETICAL,
  [configNameValues.fontSize]: '18',
  [configNameValues.textAlign]: 'left',
  [configNameValues.iconPosition]: 'top',
  [configNameValues.enableViewAll]: 'true',
  [configNameValues.hideEmptyList]: 'false',
  [configNameValues.viewAllButtonStyle]: VIEW_ALL_STYLE_ENUM.OUTLINE_BUTTON,
  [configNameValues.desktopColumnCount]: '6',
  [configNameValues.mobileColumnCount]: '2',
  [configNameValues.colorScheme]: '1'
};

const configOptions = {
  [configNameValues.headingText]: {
    label: 'Heading',
    type: INPUT_CONTROL_TYPE_ENUM.TEXT_INPUT
  },
  [configNameValues.headingSize]: {
    label: 'Heading size',
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    options: [
      {
        value: HEADING_SIZE_STYLE_ENUM.SMALL,
        label: 'Small'
      },
      {
        value: HEADING_SIZE_STYLE_ENUM.MEDIUM,
        label: 'Medium'
      },
      {
        value: HEADING_SIZE_STYLE_ENUM.LARGE,
        label: 'Large'
      },
      {
        value: HEADING_SIZE_STYLE_ENUM.EXTRA_LARGE,
        label: 'Extra large'
      },
      {
        value: HEADING_SIZE_STYLE_ENUM.HUGE,
        label: 'Huge'
      }
    ]
  },
  [configNameValues.descriptionText]: {
    type: INPUT_CONTROL_TYPE_ENUM.TEXT_INPUT,
    isTextArea: true,
    label: 'Description',
    isVisible: (config) => config.showCollectionDescription !== 'true'
  },
  [configNameValues.isSubcategoryList]: {
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX,
    label:
      'Show subcategories for a selected category. (All subcategories will be shown if no category is selected)'
  },
  [configNameValues.isSubcategoriesUnfiltered]: {
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX,
    label:
      'Show all subcategories, including those not in the selected category.',
    isVisible: (config) => config.isSubcategoryList === 'true'
  },
  [configNameValues.hideEmptyList]: {
    label:
      'Hide this section when no categories exist. (Subcategories will automatically be hidden if the selected category has none.)',
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX
  },
  [configNameValues.maximumDisplayed]: {
    label: 'Maximum categories displayed',
    type: INPUT_CONTROL_TYPE_ENUM.SLIDER,
    min: 2,
    max: 12,
    unit: ''
  },
  [configNameValues.desktopColumnCount]: {
    label: 'Desktop column count',
    type: INPUT_CONTROL_TYPE_ENUM.SLIDER,
    min: 1,
    max: 6,
    unit: ''
  },
  [configNameValues.displayType]: {
    type: INPUT_CONTROL_TYPE_ENUM.BUTTON_GROUP,
    label: 'Display type',
    options: [
      {
        value: LIST_DISPLAY_ENUM.TEXT,
        label: 'Text'
      },
      {
        value: LIST_DISPLAY_ENUM.CARD,
        label: 'Card'
      }
    ]
  },
  [configNameValues.isOutlineStyle]: {
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX,
    label: 'Outline button style',
    isVisible: (config) =>
      config[configNameValues.displayType] === LIST_DISPLAY_ENUM.CARD
  },
  [configNameValues.showIcons]: {
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX,
    label: 'Show icons',
    isVisible: (config) =>
      config[configNameValues.displayType] === LIST_DISPLAY_ENUM.CARD &&
      config.isSubcategoryList !== 'true'
  },
  [configNameValues.cardSize]: {
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    label: 'Card size',
    options: [
      {
        value: 'extra_small',
        label: 'Extra Small'
      },
      {
        value: 'small',
        label: 'Small'
      },
      {
        value: 'medium',
        label: 'Medium'
      },
      {
        value: 'large',
        label: 'Large'
      },
      {
        value: 'extra_large',
        label: 'Extra Large'
      },
      {
        value: 'huge',
        label: 'Huge'
      }
    ],
    isVisible: (config) =>
      config[configNameValues.displayType] === LIST_DISPLAY_ENUM.CARD
  },
  [configNameValues.textSortBy]: {
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    label: 'Sort by',
    options: [
      {
        value: LIST_SORT_ENUM.ALPHABETICAL,
        label: 'Alphabetical'
      },
      {
        value: LIST_SORT_ENUM.ALPHABETICAL_TOP_DOWN,
        label: 'Alphabetical (Top Down)'
      },
      {
        value: LIST_SORT_ENUM.PRODUCT_COUNT,
        label: 'Product Count'
      }
    ]
  },
  [configNameValues.textAlign]: {
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    label: 'Text alignment',
    options: [
      {
        value: 'left',
        label: 'Left'
      },
      {
        value: 'center',
        label: 'Center'
      },
      {
        value: 'right',
        label: 'Right'
      }
    ]
  },
  [configNameValues.iconPosition]: {
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    label: 'Icon position',
    options: [
      {
        value: 'top',
        label: 'Top'
      },
      {
        value: 'bottom',
        label: 'Bottom'
      }
    ],
    isVisible: (config) =>
      config[configNameValues.displayType] === LIST_DISPLAY_ENUM.CARD &&
      config[configNameValues.showIcons] === 'true' &&
      config[configNameValues.textAlign] === 'center'
  },
  [configNameValues.fontSize]: {
    label: 'Text size',
    type: INPUT_CONTROL_TYPE_ENUM.SLIDER,
    min: 10,
    max: 18,
    unit: 'px'
  },
  [configNameValues.enableViewAll]: {
    label:
      'Show a "view all" button when there are more categories than displayed',
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX
  },
  [configNameValues.viewAllButtonStyle]: {
    type: INPUT_CONTROL_TYPE_ENUM.BUTTON_GROUP,
    label: '"View all" button style',
    options: [
      {
        value: VIEW_ALL_STYLE_ENUM.LINK,
        label: 'Link'
      },
      {
        value: VIEW_ALL_STYLE_ENUM.OUTLINE_BUTTON,
        label: 'Outline button'
      },
      {
        value: VIEW_ALL_STYLE_ENUM.SOLID_BUTTON,
        label: 'Solid button'
      }
    ],
    isVisible: (config) => config.enableViewAll === 'true'
  },
  [configNameValues.categoriesFullWidth]: {
    label: 'Stretch categories full width',
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX
  },
  [configNameValues.colorScheme]: {
    label: 'Color scheme',
    type: 'entity',
    entityType: ECOM_ENTITY_TYPES.ecomStoreThemeColorScheme
  },
  [configNameValues.mobileColumnCount]: {
    type: INPUT_CONTROL_TYPE_ENUM.BUTTON_GROUP,
    label: 'Mobile column count',
    category: 'Mobile layout',
    options: [
      {
        value: '1',
        label: '1 column'
      },
      {
        value: '2',
        label: '2 columns'
      }
    ]
  }
};

const config = {
  type: ECOM_WEBSTORE_SECTION_TYPE_ENUM.CATEGORY_LIST,
  title: 'Category list',
  icon: BoxMargin,
  allowedBounds: [ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY],
  Component: WsCategoryList,
  configOptions,
  maxItemCount: 12,
  blocks: [
    {
      ...ECOM_WEBSTORE_SECTION_BLOCKS[
        ECOM_WEBSTORE_SECTION_BLOCK_TYPE_ENUM.CATEGORY
      ],
      defaultCount: 0,
      maxCount: 12
    }
  ],
  defaultConfig: configDefaultValues
};

export { config, configOptions, configNameValues, configDefaultValues };
